import { getContractMultiplier } from '../../constants/contractMultipliers';
import { getCanonicalAssetClass } from './canonicalAssetClass';
import { buildTradeDetailsDeterministicSummary } from './tradeDetailsDeterministicAdapter';
import { computeDeterministicPositionState, type PrimitiveExecution } from './deterministicPrimitives';
import type { MetricExecution } from './deterministicTradeMetrics';

type TradeLike = Record<string, unknown>;

export type JournalResolvedPnl = {
  status: 'open' | 'closed';
  grossPnl: number | null;
  netPnl: number | null;
  executionCount: number;
};

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function readFiniteNumber(record: TradeLike, keys: string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function normalizeExecutionSide(value: unknown, fallbackQuantity: unknown): 'BUY' | 'SELL' | null {
  const raw = String(value ?? '').trim().toLowerCase();
  const compact = raw.replace(/[\s_-]+/g, '');
  if (['s', 'sell', 'short', 'sellshort', 'shortsell'].includes(compact) || raw.includes('sell') || raw.includes('short')) return 'SELL';
  if (['b', 'buy', 'long', 'buytocover', 'cover'].includes(compact) || raw.includes('buy') || raw.includes('long')) return 'BUY';

  const quantity = Number(fallbackQuantity);
  if (Number.isFinite(quantity) && quantity !== 0) return quantity < 0 ? 'SELL' : 'BUY';
  return null;
}

function getExecutionCollections(trade: TradeLike): unknown[] {
  const collections = [trade.executionsList, trade.executions, trade.fills, trade.mergedExecutions];
  for (const collection of collections) {
    if (Array.isArray(collection) && collection.length > 0) return collection;
  }
  return [];
}

function normalizeExecutions(trade: TradeLike): MetricExecution[] {
  return getExecutionCollections(trade)
    .map((entry, index): MetricExecution | null => {
      if (!entry || typeof entry !== 'object') return null;
      const row = entry as TradeLike;
      const timestamp = readFiniteNumber(row, ['timestamp', 'time', 'executedAt', 'openTimestampMs', 'executionTimestamp']);
      const price = readFiniteNumber(row, ['price', 'avgPrice', 'fillPrice', 'executionPrice']);
      const rawQuantity = row.qty ?? row.quantity ?? row.volume ?? row.shares;
      const qty = Math.abs(Number(rawQuantity));
      const side = normalizeExecutionSide(row.side ?? row.execSide ?? row.type ?? row.direction ?? row.position, rawQuantity);
      if (timestamp == null || price == null || !Number.isFinite(qty) || qty <= 0 || side == null) return null;

      return {
        id: String(row.id ?? row.executionId ?? row.storageId ?? `execution-${index}`),
        timestamp,
        side,
        qty,
        price,
        fee: readFiniteNumber(row, ['commissionAndFees', 'commissionsAndFees', 'fee', 'fees', 'commission', 'rowFees']) ?? 0,
      };
    })
    .filter((execution): execution is MetricExecution => execution != null);
}

function resolveMultiplier(trade: TradeLike): number {
  const assetClass = getCanonicalAssetClass(trade);
  if (assetClass !== 'FUTURES') return 1;

  const firstExecution = getExecutionCollections(trade).find((entry) => entry && typeof entry === 'object') as TradeLike | undefined;
  const symbol = String(trade.symbol ?? firstExecution?.symbol ?? '');
  return getContractMultiplier(symbol, 'FUTURES', trade.instrument ?? firstExecution?.instrument);
}

export function resolveJournalExecutionPnl(trade: unknown): JournalResolvedPnl | null {
  if (!trade || typeof trade !== 'object') return null;
  const tradeLike = trade as TradeLike;
  const executions = normalizeExecutions(tradeLike);
  if (executions.length === 0) return null;

  const multiplier = resolveMultiplier(tradeLike);
  const primitiveExecutions: PrimitiveExecution[] = executions.map((execution, index) => ({
    id: execution.id,
    timestamp: execution.timestamp,
    side: execution.side,
    qty: execution.qty,
    price: execution.price,
    fee: execution.fee,
    originalIndex: index,
  }));

  const position = computeDeterministicPositionState({
    executions: primitiveExecutions,
    multiplier,
    markPrice: null,
  });

  if (position.applied.length === 0) return null;
  if (position.side !== 'flat') {
    return {
      status: 'open',
      grossPnl: null,
      netPnl: null,
      executionCount: position.applied.length,
    };
  }

  const summary = buildTradeDetailsDeterministicSummary({
    executions,
    bars: [],
    multiplier,
    clampEfficiency: false,
    debug: false,
  });

  return {
    status: 'closed',
    grossPnl: round2(summary.grossPnl),
    netPnl: round2(summary.netPnl),
    executionCount: position.applied.length,
  };
}
